import { useContext, useEffect } from "react";
import { matchRoutes, useLocation } from "react-router";

import { OrganizationContext } from "@/contexts/frontend/organization/OrganizationContext";

/**
 * Sets `document.title` to `${pageName} · ${suffix}`. Suffix is "Admin" under
 * `/admin`, the org name under `/organizations`, otherwise "InsureScope" (the
 * user-facing brand; the codebase name is "ContractScope"). Pages supply only
 * their own label so the suffix rule lives here.
 *
 * Uses `useContext` directly so the hook works on auth and admin pages where
 * `OrganizationContext` is absent; `useOrganization()` would throw.
 */
export function useDocumentTitle(pageName: string): void {
    const { pathname } = useLocation();
    const orgName = useContext(OrganizationContext)?.organization.name;

    useEffect(() => {
        document.title = `${pageName} · ${suffixFor(pathname, orgName)}`;
    }, [pageName, pathname, orgName]);
}

const BRAND = "InsureScope";

function suffixFor(pathname: string, orgName: string | undefined): string {
    const routes = [
        { path: "/admin/*", suffix: "Admin" },
        { path: "/organizations/*", suffix: orgName ?? BRAND },
    ];
    return matchRoutes(routes, { pathname })?.[0]?.route.suffix ?? BRAND;
}
